import PropTypes from 'prop-types';
import { Badge } from '@/components/ui/badge';
import { useLeads } from '@/hooks/use-leads';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';

const statusColors = {
  'Not Contacted': "bg-yellow-100 text-yellow-800",
  'Contacted': "bg-blue-100 text-blue-800",
  'Qualified': "bg-green-100 text-green-800",
  'Converted': "bg-purple-100 text-purple-800",
  'Lost': "bg-red-100 text-red-800",
  'Pending': "bg-gray-100 text-gray-800"
};

const priorityIndicators = {
  'Not Contacted': "bg-red-500",
  'Contacted': "bg-orange-400",
  'Qualified': "bg-green-500",
  'Converted': "bg-purple-500",
  'Lost': "bg-gray-400",
  'Pending': "bg-gray-300"
};

const LeadPipeline = ({ maxLeads = 4 }) => {
  const navigate = useNavigate();
  const { leads, loading, totalItems } = useLeads();
  const recentLeads = leads?.slice(0, maxLeads) || [];

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="p-3 flex justify-center items-center min-h-[120px]">
        <Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-base font-semibold text-gray-900">Leads</h2>
        <Link to="/leads" className="text-xs text-orange-500 hover:text-orange-600">
          View All {totalItems > 0 ? `(${totalItems})` : ''}
        </Link>
      </div>

      {recentLeads.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-xs">No leads available</p>
        </div>
      ) : (
        <div className="space-y-2">
          {recentLeads.map((lead) => {
            const status = lead.Lead_status || 'Pending';

            return (
              <div
                key={lead._id}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                onClick={() => navigate('/leads')}
              >
                {/* Priority Indicator */}
                <div className={`h-2 w-2 rounded-full flex-shrink-0 ${priorityIndicators[status] || 'bg-gray-300'}`} />

                {/* Avatar */}
                <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                  {getInitials(lead.fullName)}
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {lead.fullName}
                  </p>
                </div>

                {/* Status Badge */}
                <Badge className={`${statusColors[status] || 'bg-gray-100 text-gray-800'} text-xs px-1.5 py-0.5 font-normal`}>
                  {status.split(' ')[0]}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

LeadPipeline.propTypes = {
  maxLeads: PropTypes.number
};

export default LeadPipeline;
